import { useMemo } from 'react';

import type { VideoSize } from '#/types/Video/VideoSize';
import { getVideoData } from '#/utils/video/getVideoData';
import { useVideoSize } from '#/utils/video/useVideoSize';

import {
  VideoPlayerContainer,
  VideoPlayerIframe,
  VideoPlayerVideo,
} from './VideoPlayer.styled';
import type { VideoPlayerOptions } from './VideoPlayerOptions';
import { getVimeoVideoSrc } from './helpers/getVimeoVideoSrc';
import { getYoutubeVideoSrc } from './helpers/getYoutubeVideoSrc';

type Props = {
  url: string;
  size: VideoSize;
  autoPlay?: boolean;
  muted?: boolean;
  qaId?: string;
  options?: VideoPlayerOptions;
};

export function VideoPlayer({
  url,
  size,
  autoPlay,
  muted,
  qaId,
  options,
}: Props) {
  const { width, height } = useVideoSize(size);

  const videoData = useMemo(() => getVideoData({ url }), [url]);

  const src: string | undefined = useMemo(() => {
    if (!videoData) return undefined;

    switch (videoData.type) {
      case 'raw':
        return videoData.url;
      case 'youtube':
        return getYoutubeVideoSrc({
          videoId: videoData.videoId,
          autoPlay,
          muted,
          showRelatedVideos: options?.showYoutubeRelatedVideos,
        });
      case 'vimeo':
        return getVimeoVideoSrc({
          videoId: videoData.videoId,
          autoPlay,
          muted,
        });
    }
  }, [autoPlay, muted, options?.showYoutubeRelatedVideos, videoData]);

  const videoPlayer = useMemo(() => {
    if (!videoData || !src) return 'Invalid video url';

    if (videoData.type === 'raw') {
      return (
        <VideoPlayerVideo
          src={src}
          controls
          autoPlay={autoPlay}
          muted={muted}
          data-qa-id={qaId}
        />
      );
    }

    return (
      <VideoPlayerIframe
        title="video"
        src={src}
        allowFullScreen
        allow="autoplay; fullscreen"
        data-qa-id={qaId}
        $videoDataType={videoData.type}
      />
    );
  }, [autoPlay, muted, qaId, src, videoData]);

  return (
    <VideoPlayerContainer style={{ width, height }}>
      {videoPlayer}
    </VideoPlayerContainer>
  );
}
